/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { DeviceTimeInfo } from '../models/DeviceTimeInfo';
import type { Timestamp } from '../models/Timestamp';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class HstpService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the current device time information.
     * Get the current device time information.
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any DeviceTimeResponse
     * @throws ApiError
     */
    public getDeviceTimeInfo(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceTimeInfo;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hstp/info',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            errors: {
                400: `Bad request.`,
            },
        });
    }

    /**
     * Initiate a server-device clock synchronization.
     * Initiate a server-device clock synchronization.
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @param s If true, the clock sync response will be synchronous and the sync result will be returned in the response, in addition to published on the SSE stream. If false, the sync result will only be published on the SSE stream.
     * @returns any DeviceTimeResponse
     * @throws ApiError
     */
    public clockSync(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
        s: boolean = false,
    ): CancelablePromise<(DeviceResponse & {
        result?: DeviceTimeInfo;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hstp/clocksync',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
                's': s,
            },
            errors: {
                400: `Bad request.`,
            },
        });
    }

    /**
     * Set the user adjusted device time offset.
     * Set the user adjusted device time offset.
     * @param xConnectionKey Device connection key.
     * @param requestBody Offset value
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any DeviceTimeResponse
     * @throws ApiError
     */
    public setOffset(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The offset value in milliseconds.
             */
            offset: number;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: string;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hstp/offset',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request.`,
            },
        });
    }

    /**
     * Get the device time offset.
     * Get the user adjusted device time offset.
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any DeviceTimeResponse
     * @throws ApiError
     */
    public getOffset(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: {
            offset: Timestamp;
        };
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hstp/offset',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            errors: {
                400: `Bad request.`,
            },
        });
    }

}
